<template>
  <expandable-box-node v-model="opened" title="ACT - Asthma Control Test" questionkey="ACT">
    <div class="d-flex flex-column gap-6">
      <act-general-questions v-if="patientData.age >= 12" v-model="modelValue" :disabled="disabled" />
      <act-child-questions v-else v-model="modelValue" :disabled="disabled" />
      <score-board :score="score" :labels="scoreLabels" />
    </div>
  </expandable-box-node>
</template>

<script>
import { WatchOptionsWithHandler, PropOptions } from 'vue'
import { Computed, ObjectLiteral } from '@/_typedefs'
import { createNamespacedHelpers } from 'vuex'
import ScoreBoard from '../partial/Score/ScoreBoard.vue'
import ExpandableBoxNode from '../../ExpandableBox/ExpandableBoxNode.vue'
import ActGeneralQuestions from './ActGeneralQuestions.vue'
import ActChildQuestions from './ActChildQuestions.vue'
import { cloneDeep } from 'lodash'

const {
  mapActions: newAttendanceFormActions,
  mapMutations: newAttendanceFormMutations,
  mapGetters: newAttendanceFormGetters,
} = createNamespacedHelpers('newAttendanceForm')

/**
 * @typedef {Record<string, number?>} ModelValue
 */

export default {
  components: {
    ScoreBoard,
    ExpandableBoxNode,
    ActGeneralQuestions,
    ActChildQuestions,
  },
  props: {
    /** @type {PropOptions<boolean>} */
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    /** @type {ModelValue} */
    const modelValue = {}

    const scoreLabels = [
      {
        text: '21-25, Asma controlada',
        highlightFn: (score) => score >= 21 && score <= 25,
        highlightColor: '#73B71E',
      },
      {
        text: '16-20, Asma parcialmente controlada',
        highlightFn: (score) => score >= 16 && score <= 20,
        highlightColor: '#B2921D',
      },
      {
        text: '0-15, Asma não controlada',
        highlightFn: (score) => !!score && score <= 15,
        highlightColor: '#B3261E',
      },
    ]

    return {
      modelValue,
      scoreLabels,
      opened: false,
    }
  },
  computed: {
    .../** @type {{patientData: Computed<ObjectLiteral>}} */ (newAttendanceFormGetters(['patientData'])),
    /** @type {Computed<number?>} */
    score() {
      const values = Object.values(this.modelValue).filter((value) => value !== null)
      if (values.length < 5) {
        return null
      }
      return values.reduce((acc, curr) => acc + curr, 0)
    },
  },
  methods: {
    ...newAttendanceFormActions(['getParameter']),
    ...newAttendanceFormMutations(['updateParameter']),
    openQuestion() {
      this.opened = true
    },
  },
  watch: {
    /** @type {WatchOptionsWithHandler<ModelValue>} */
    modelValue: {
      handler(value) {
        const { text: scoreLabel } = this.scoreLabels.find((scoreLabel) => scoreLabel.highlightFn(this.score)) ?? {}
        if (!scoreLabel) {
          return
        }

        this.updateParameter({ parameter: 'act', value: { score: this.score, label: scoreLabel, questions: value } })
      },
      deep: true,
    },
  },
  async created() {
    const { questions } = (await this.getParameter('act')) ?? {}
    if (questions) {
      this.modelValue = cloneDeep(questions)
    }

    this.$eventBus.on(`open-question-ACT`, this.openQuestion)
    this.$eventBus.emit(`question-ACT-loaded`)
  },
  beforeDestroy() {
    this.$eventBus.off(`open-question-ACT`, this.openQuestion)
  },
}
</script>

<style></style>
